import { useCallback, useMemo, useState } from 'react'
import { Repeat, Webhook } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchWebhookDeliveries,
  resendWebhook,
  DT_STR as S,
  type WebhookDeliveryRow,
} from './devtoolsApi'

/**
 * A27.03 — Отладка webhook. Webhook delivery log (event/url/status/attempt) with
 * a per-row "resend". Dormant until `/admin/devtools/webhook-deliveries` ships —
 * fetcher fail-soft. Placeholder permission USERS_VIEW.
 */
export function WebhookDebugScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<WebhookDeliveryRow>[]>(
    () => [
      { key: 'at', header: S.at, type: 'text' },
      { key: 'event', header: S.eventCol, type: 'text' },
      { key: 'url', header: S.url, type: 'text' },
      { key: 'status_code', header: S.statusCode, type: 'number' },
      { key: 'attempt', header: S.attempt, type: 'number' },
      { key: 'duration_ms', header: S.duration, type: 'number' },
      {
        key: 'ok',
        header: S.status,
        type: 'enum',
        options: [
          { value: 'true', label: S.ok, status: 'active' },
          { value: 'false', label: S.failed, status: 'rejected' },
        ],
        accessor: (r) => String(r.ok),
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<WebhookDeliveryRow>[]>(
    () => [
      {
        id: 'resend',
        label: S.resend,
        icon: <Repeat size={14} />,
        onSelect: async (r) => {
          try {
            await resendWebhook(r.id)
            toast(S.resent, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchWebhookDeliveries, [])
  return (
    <>
      <PageHeader title={S.webhookDebug} screenId="A27.03" />
      <DataTable<WebhookDeliveryRow>
        key={reloadKey}
        screenId="A27.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Webhook size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
